import { Button, CloseButton, Popover, Skeleton } from '@mantine/core';
import { IconMail, IconSwitch3, IconBrandGoogleFilled } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { TestAccountSelect } from '../../../newStep/TestAccountSelect';
import type { SpecialLoginPart } from '../converters';
import { modals } from '@mantine/modals';

export default function SpecialLoginPart({
  part,
  readonly,
  toggleEditing,
  updateFunc,
}: {
  part: SpecialLoginPart;
  readonly?: boolean;
  toggleEditing: (editing: boolean) => void;
  updateFunc: (part: Omit<SpecialLoginPart, 'type'>) => Promise<void>;
}) {
  const projectId = useProjectId();

  const [editing, setEditing] = useState(false);

  const { data: selectedAccount, isLoading: selectedAccountIsLoading } = api.testAccount.get.useQuery({
    projectId: projectId || '',
    accountId: part.testAccountId,
  });

  const handleTestAccountChange = () => {
    if (readonly) {
      return;
    }
    handleClosePopover();
    modals.open({
      withCloseButton: false,
      size: 'sm',
      children: (
        <TestAccountSelect
          onSubmit={async (testAccountId: string) => {
            await updateFunc({ ...part, testAccountId });
          }}
          title="Select Test Account for Login"
          submitButtonText="Use Account"
          filter={part.loginType === 'GOOGLE' ? (account) => account.type === 'GOOGLE' : undefined}
        />
      ),
    });
  };

  const handleClosePopover = useCallback(() => {
    toggleEditing(false);
    setEditing(false);
  }, [toggleEditing]);

  return (
    <Popover
      width={400}
      position="bottom"
      withArrow
      shadow="md"
      opened={editing}
      closeOnClickOutside={true}
      onClose={handleClosePopover}
      onDismiss={handleClosePopover}
    >
      <Popover.Target>
        <div
          className="gap-2 break-all rounded-md hover:cursor-pointer"
          onDoubleClick={() => {
            if (!readonly) {
              toggleEditing(true);
              setEditing(true);
            }
          }}
        >
          Log in to {part.loginType} with{' '}
          <span className="text-yellow-900 hover:cursor-pointer dark:text-yellow-600">
            {selectedAccount?.name || 'Unknown Account'}{' '}
          </span>
        </div>
      </Popover.Target>

      <Popover.Dropdown
        className="dark:bg-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col">
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-medium">Configure Login Test Account</div>
            <CloseButton
              variant="transparent"
              size="md"
              onClick={handleClosePopover}
            />
          </div>

          <div>
            <div className="flex flex-col gap-2">
              {selectedAccountIsLoading ?
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-[25px] rounded-lg" />
                  <Skeleton className="h-[25px] rounded-lg" />
                  <Skeleton className="h-[25px] rounded-lg" />
                </div>
              : selectedAccount ?
                <div>
                  <div className="mb-2 text-sm font-medium">Test Account Details</div>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{selectedAccount.name}</div>
                    {selectedAccount.type === 'GOOGLE' ?
                      <IconBrandGoogleFilled
                        size={16}
                        className="text-gray-500"
                      />
                    : <IconMail
                        size={16}
                        className="text-gray-500"
                      />
                    }
                  </div>
                  {!!selectedAccount.description && (
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{selectedAccount.description}</div>
                  )}
                  <div>{selectedAccount.userName}</div>
                  <div className="italic">{selectedAccount.password}</div>
                </div>
              : <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                  <div className="text-sm text-red-600 dark:text-red-400">No account selected</div>
                </div>
              }
            </div>
          </div>

          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            onClick={handleTestAccountChange}
            leftSection={<IconSwitch3 size={16} />}
            disabled={readonly || selectedAccountIsLoading}
            className="w-full"
          >
            Change Account
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
